// eslint-disable-next-line max-classes-per-file
import { FocusKeyManager, FocusableOption } from '@angular/cdk/a11y';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    HOST_TAG_NAME,
    OnDestroy,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    contentChildren,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Observable, merge, startWith, switchMap, tap } from 'rxjs';

import { DsIosActiveSupportDirective } from './ios-active-support.directive';
import { LIST_ROLES } from './list-roles';

export const DS_LISTITEM_SIZE_ARRAY = ['medium', 'medium-condensed', 'large', 'large-condensed'] as const;
export type DsListItemSize = (typeof DS_LISTITEM_SIZE_ARRAY)[number];
export const DS_LIST_GROUP_ROLE_ARRAY = Object.keys(LIST_ROLES) as (keyof typeof LIST_ROLES)[];
export type DsListGroupRole = keyof typeof LIST_ROLES;

@Component({
    selector: 'ds-list-item, a[ds-list-item]',
    hostDirectives: [DsIosActiveSupportDirective],
    template: ` <ng-content select="[slot=start]" />
        @if (showCenterSlot()) {
            <div class="ds-list-tile-text">
                <span class="ds-list-tile-title" [class.ds-list-title-strong]="boldTitle()">{{ title() }}</span>
                @if (subtitle()) {
                    <span class="ds-list-tile-sub-title">{{ subtitle() }}</span>
                }
                @if (subtext()) {
                    <span class="ds-list-tile-sub-text">{{ subtext() }}</span>
                }
            </div>
        }
        <div class="ds-list-end-slot">
            <ng-content select="[slot=end]" />
        </div>`,
    styleUrl: './list-item.component.scss',
    host: {
        'class': 'ds-list-item',
        '[class.ds-list-item-inverse]': 'inverse()',
        '[class]': '"ds-list-item-" + size()',
        '[attr.role]': 'role()',
        '[class.ds-list-item-interactive]': 'interactive()',
        '[class.ds-list-item-selected]': 'selected()',
        '[attr.aria-selected]': 'selectable() ? selected() : null',
        '[attr.tabindex]': '(interactive() && listGroup) ? tabIndex() : null',
        '(click)': 'handleClick($event)',
        '(focus)': 'handleFocus()',
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class DsListItem implements FocusableOption {
    public readonly title = input.required<string>();
    public readonly subtitle = input<string>('');
    public readonly subtext = input<string>('');
    public readonly selected = model<boolean>(false);
    public readonly inverse = input(false, { transform: booleanAttribute });
    public readonly boldTitle = input(true, { transform: booleanAttribute });
    public readonly showCenterSlot = input(true, { transform: booleanAttribute });
    public readonly size = input<DsListItemSize>('large');
    private readonly tagName: string | null = inject(HOST_TAG_NAME, { optional: true });

    public readonly itemClick = output<DsListItem>();
    public readonly itemFocus = output<DsListItem>();

    public readonly itemClick$ = outputToObservable<DsListItem>(this.itemClick);
    public readonly itemFocus$ = outputToObservable<DsListItem>(this.itemFocus);

    private readonly elementRef = inject(ElementRef);
    private readonly listGroup = inject(DsListGroup, { optional: true });

    protected readonly tabIndex = signal(-1);

    protected readonly role = computed(() => {
        const roleType = this.listGroup?.roleType();
        return roleType && LIST_ROLES[roleType] ? LIST_ROLES[roleType].child : null;
    });

    isAnchor() {
        return this.tagName === 'a';
    }

    selectable() {
        if (!this.listGroup) {
            return false;
        }

        const role = this.listGroup.roleType();
        return role ? LIST_ROLES[role].supportsSelection : false;
    }

    interactive() {
        if (!this.listGroup) {
            return true;
        }

        const role = this.listGroup.roleType();
        return role ? LIST_ROLES[role].supportsInteraction || this.isAnchor() : false;
    }

    focus(): void {
        this.elementRef.nativeElement.focus();
    }

    setTabIndex(index: number): void {
        this.tabIndex.set(index);
    }

    handleClick($event: MouseEvent): void {
        const target = $event.target as HTMLElement;

        if (this.isAnchor() || (target as HTMLInputElement)?.type === 'checkbox') {
            return;
        }

        this.itemClick.emit(this);
    }

    handleFocus(): void {
        if (this.interactive()) {
            this.itemFocus.emit(this);
        }
    }
}

@Component({
    selector: 'ds-list-group',
    standalone: true,
    template: ` <ng-content />`,
    host: {
        '[attr.role]': 'role()',
        '[attr.aria-multiselectable]': 'ariaMultiSelectable()',
        '(keydown)': 'handleKeydown($event)',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsListGroup implements AfterViewInit, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);

    public readonly selectionMode = input<'single' | 'multiselect'>('single');
    public readonly roleType = input<DsListGroupRole | null>(null);
    public readonly selectedItems = signal<DsListItem[]>([]);

    protected readonly items = contentChildren(DsListItem, { descendants: true });

    protected readonly supportsSelection = computed(() => {
        const role = this.roleType();
        return role ? LIST_ROLES[role].supportsSelection : false;
    });
    protected readonly supportsInteraction = computed(() => {
        const role = this.roleType();
        const hasAnchorItems = this.items().some((item) => item.isAnchor());
        return role ? LIST_ROLES[role].supportsInteraction || hasAnchorItems : false;
    });
    protected readonly ariaMultiSelectable = computed(() => {
        if (!this.supportsSelection() || this.items().length === 0) {
            return null;
        }

        return this.selectionMode() === 'multiselect' ? 'true' : 'false';
    });
    protected readonly role = computed(() => {
        const roleType = this.roleType();
        return roleType && LIST_ROLES[roleType] ? LIST_ROLES[roleType].parent : null;
    });

    protected readonly itemsChanges$ = toObservable(this.items);
    public keyManager!: FocusKeyManager<DsListItem>;
    public readonly hasInitialized = model(false);

    ngAfterViewInit() {
        this.itemsChanges$
            .pipe(
                startWith(this.items()),
                tap(() => this.setupKeyManager()),
                switchMap(() => this.setupEventListeners()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.keyManager?.destroy();
    }

    private setupClickListeners(): Observable<unknown> {
        return merge(
            ...this.items().map((item) =>
                item.itemClick$.pipe(
                    tap((clickedItem) => {
                        if (this.supportsSelection()) {
                            this.selectItem(clickedItem);
                        }
                        if (this.supportsInteraction()) {
                            this.keyManager.setActiveItem(clickedItem);
                        }
                    }),
                ),
            ),
        );
    }

    private setupFocusListeners(): Observable<unknown> {
        return merge(
            ...this.items().map((item) =>
                item.itemFocus$.pipe(
                    tap((focusedItem) => {
                        if (this.supportsInteraction()) {
                            this.keyManager.setActiveItem(focusedItem);
                        }
                    }),
                ),
            ),
        );
    }

    private setupEventListeners(): Observable<unknown> {
        return merge(this.setupClickListeners(), this.setupFocusListeners());
    }

    private setupKeyManager(): void {
        const interactiveItems = this.items().filter((item) => item.interactive());

        if (this.keyManager) {
            this.keyManager.destroy();
        }

        this.keyManager = new FocusKeyManager(interactiveItems).withVerticalOrientation().withWrap();

        if (interactiveItems.length > 0) {
            interactiveItems.forEach((item) => item.setTabIndex(-1));
            interactiveItems[0]?.setTabIndex(0);

            if (this.supportsSelection() && !this.hasInitialized()) {
                this.hasInitialized.set(true);
                interactiveItems.forEach((item) => {
                    if (item.selected()) {
                        this.selectItem(item);
                    }
                });
            }

            this.keyManager.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeIndex: number) => {
                if (activeIndex !== -1) {
                    interactiveItems.forEach((item, i) => {
                        item.setTabIndex(i === activeIndex ? 0 : -1);
                    });
                }
            });
        }
    }

    handleKeydown(event: KeyboardEvent) {
        if (this.supportsInteraction()) {
            this.keyManager.onKeydown(event);

            if (['Enter', 'Space'].includes(event.code)) {
                const activeItem = this.keyManager.activeItem;
                if (this.supportsSelection() && activeItem) {
                    this.selectItem(activeItem);
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        }
    }

    selectItem(item: DsListItem) {
        if (!this.supportsSelection()) {
            return;
        }

        const selectedSet = this.selectedItems();
        const wasSelected = selectedSet.includes(item);

        if (this.selectionMode() === 'single' && wasSelected && selectedSet.length === 1) {
            return;
        }

        if (this.selectionMode() === 'single') {
            selectedSet.forEach((i) => i.selected.set(false));
            item.selected.set(true);
            this.selectedItems.set([item]);
        } else {
            const items = wasSelected ? selectedSet.filter((i) => i !== item) : [...selectedSet, item];

            item.selected.set(!wasSelected);
            this.selectedItems.set(items);
        }
    }
}
